import React, { useState, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Typography,
  Grid,
  Divider,
  makeStyles,

} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Rider } from 'Agent/Policies/api';
import { toKebabCase } from 'Shared/utilities/stringFormatHelpers';
import { toMoneyValue } from 'Shared/utilities/FormatMoney';
import { toFormattedDateAsUTC } from 'Shared/utilities/DateFormatter';
import { CardFieldV2 } from 'Shared/components/CardFieldV2';


export interface PlanBenefitLifeProps {
  riders?: Rider[];
}

const useStyles = makeStyles(theme => ({
  root: { width: '100%', marginTop: theme.spacing(2) },
  accordion: {
    border: '1px solid #E2E6EF',
    borderRadius: 12,
    background: '#FFFFFF',
    boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
    '&:before': { display: 'none' },
    '&.Mui-expanded': { boxShadow: '0 4px 10px rgba(0,0,0,0.08)' },
  },
  summary: {
    padding: theme.spacing(2.50, 2),
    '& .MuiAccordionSummary-content': { margin: 0, alignItems: 'center' },
  },
  title: {
    fontWeight: 600, color: '#0033A0', marginBottom: '1rem',
    borderBottomLeftRadius: '4PX',
    borderBottomRightRadius: '4px',
    backgroundColor: '#f1f1f1',

    width: '150%',
    fontSize: '1.10rem',
    margin: '0',
    textAlign: 'left',
    borderBottom: '2px solid #ccc',
    padding: '1.0rem 1.0rem',
  },
  list: { width: '100%', padding: 0 },
  listItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0.0, 0.00),
    alignItems: 'stretch',
    '&:hover': { background: '#F5F8FC' },
  },
  container: {
    justifyContent: 'flex-start',
    margin: 0,
    padding: '1rem',
    width: '100%',
    borderBottom: '1px solid #ccc',
  },
  fieldItem: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  spacer: {
    flexBasis: '120%',
  },
  detailsOuter: {
    padding: 0,
  },


  riderHeader: {
    fontWeight: 600,
    fontSize: '.9rem',
    color: '#0D1941',
    margin: '0 0 .5rem',
  },
  gridContainer: {
    margin: 0,
    width: '100%',
  },
  divider: { margin: 0, '&:last-of-type': { display: 'none' } },
}));

interface NormalizedRider {
  riderName: string;
  riderCode?: string;
  riderAmount?: number;
  effectiveDate?: string | Date;
  terminationDate?: string | Date;
}

const PlanBenefitLife = ({ riders }: PlanBenefitLifeProps) => {

  const classes = useStyles();
  const safe = Array.isArray(riders) ? riders : [];
  const isEmpty = safe.length === 0;



  const [expanded, setExpanded] = useState(false);
  const handleToggle = useCallback(() => setExpanded(prev => !prev), []);
  if (isEmpty) return null;

  const normalized: NormalizedRider[] = safe.map(r => {
    const anyR = r as any;
    const riderName =
      r.riderName ??
      anyR.planDescription ??
      anyR.description ??
      anyR.name ??
      'Rider';
    const riderCode =
      r.riderCode ??
      anyR.planCode ??
      anyR.code ??
      undefined;
    const riderAmount: number | undefined =
      (typeof r.riderAmount === 'number'
        ? r.riderAmount
        : typeof anyR.benefitAmount === 'number'
          ? anyR.benefitAmount
          : undefined);
    const effectiveDate =
      r.effectiveDate ??
      anyR.effectiveDate ??
      anyR.startDate;

    return {
      riderName,
      riderCode,
      riderAmount,
      effectiveDate,

    };
  });

  return (
    <div className={classes.root}>
      <Accordion
        expanded={expanded}
        onChange={handleToggle}
        className={classes.accordion}
        TransitionProps={{ unmountOnExit: true }}
        elevation={0}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon style={{ color: '#0033A0' }} fontSize="small" />
          }
          aria-controls="life-riders-content"
          id="life-riders-header"
          className={classes.summary}
        >

          <Typography component="h3" className={classes.title}>
            Benefit Riders
          </Typography>

        </AccordionSummary>
        <AccordionDetails className={classes.detailsOuter}>
          <List className={classes.list}>
            {normalized.map((r, idx) => {
              const key = toKebabCase(r.riderCode || r.riderName || `rider-${idx}`);
              return (
                <React.Fragment key={key}>
                  <ListItem className={classes.listItem}>


                    <Grid container spacing={4} className={classes.container}>
                      <Grid item xs={12} sm={6} md={3} className={classes.fieldItem}>
                        <CardFieldV2 name="Description" value={r.riderName} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} className={classes.fieldItem}>

                        <CardFieldV2 name="Plan Code" value={r.riderCode} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} className={classes.fieldItem}>
                        <CardFieldV2

                          name="Insurance Amount"
                          value={
                            typeof r.riderAmount === 'number'
                              ? toMoneyValue(r.riderAmount)
                              : '-'
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} className={classes.fieldItem}>
                        <CardFieldV2
                          name="Effective Date"
                          value={
                            r.effectiveDate
                              ? toFormattedDateAsUTC(r.effectiveDate)
                              : '-'
                          }
                        />
                      </Grid>
                    </Grid>
                  </ListItem>
                  {idx < normalized.length - 1 && (
                    <Divider className={classes.divider} component="li" />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};


export default PlanBenefitLife;